import { faCommentAlt } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DatePicker, Form, Space, Tabs, Typography } from 'antd'
import dayjs from 'dayjs'
import { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import useFeatureFlag from '../../hooks/useFeatureFlag'
import { projectTypeName, ProjectStatuses, ProjectTypes } from '../../common/constants'
import { JOB_DETAILS_CLEANUP_FEATURE, LegacyJobData, LegacyReceiptData, VisitReviewedState } from '../../domain/jobs/jobs'
import { usePrintJobReportMutation, useVisitsQuery } from '../../domain/jobs/jobsQueries'
import { WorkareaGroupWithWorkareas } from '../../domain/workareas/group'
import AttachmentUpload from '../../components/attachments/AttachmentUpload'
import { useAttachmentsInFolder } from '../../components/attachments/attachmentsQueries'
import { ReceiptTable } from '../../components/Items/Receipts/ReceiptTable'
import { useActiveVendorsListQuery } from '../../components/Vendors/vendorsQueries'
import VSpace from '../../components/common/VSpace'
import CatalogBrowser from '../../components/ItemCatalog/CatalogBrowser'
import ToggleCatalogBrowserButton from '../../components/ItemCatalog/ToggleCatalogBrowserButton'
import AddVisitButton from '../../components/Items/AddVisitButton'
import AnalysisFilter from '../../components/Items/AnalysisFilter/AnalysisFilter'
import CostingAnalysisItems from '../../components/Items/CostingAnalysis/CostingAnalysisItems'
import { ItemsContext } from '../../components/Items/ItemsRoot'
import JobCosting from '../../components/Items/JobCosting/JobCosting'
import PrintItemsSummaryModal from '../../components/Items/ItemSummary/PrintItemsSummaryModal'
import TimeAnalysis from '../../components/Items/TimeAnalysis/TimeAnalysis'
import ViewJobSummaryButton from '../../components/Items/ViewJobSummaryButton'
import JobInvoicing from '../../components/invoicing/JobInvoicing'
import JobDetailsForm from '../../components/jobs/JobDetailsForm'
import { CompletedDateField } from '../../components/jobs/JobDetailsSections'
import { UpdateJobDetailsRequest } from '../../components/jobs/jobsApi'
import JobPlan from '../../components/jobs/JobPlan/JobPlan'
import JobScheduleTable from '../../components/jobs/JobSchedule/JobScheduleTable'
import AutomationFollowUpIcon from '../../components/jobs/Proposals/AutomationsFollowUpIcon'
import Proposals from '../../components/jobs/Proposals/Proposals'
import LinkCostCodeToQuickBooks from '../../components/quickbooks/LinkCostCodeToQuickBooks'
import LinkCustomerToQuickBooks from '../../components/quickbooks/LinkCustomerToQuickBooks'
import EditVisitModal from '../../components/visits/EditVisitModal'
import VisitsPriceSummary from '../../components/visits/VisitsPriceSummary'
import VisitsTable from '../../components/visits/VisitsTable'
import JobHero from './JobHero'
import ReceiptModal from './ReceiptModal'
import SelectJobReportModal from './SelectJobReportModal'
import VisitNotesModal from './VisitNotesModal'
import { buildItemsChangeSetForSave, SaveJobOptions } from './jobSaveModel'
import {
  createNewReceipt,
  filterReceiptAttachments,
  receiptAttachmentFileName,
  toggleReceiptRemoved,
  upsertReceipt
} from './receiptsModel'
import {
  buildPrintWorkareas,
  buildPrintWorkareasRequest,
  jobPrintRouteSegment,
  PrintWorkarea,
  togglePrintWorkarea
} from './jobPrintModel'
import {
  AnalysisFilters,
  applyCostCodeQboLink,
  buildAnalysisVisitRows,
  buildDefaultAnalysisFilters,
  buildJobAnalysis,
  buildTabKeys,
  JobAnalysis,
  JobDetailTabKey,
  JobDetailTabKeys,
  showVisitNotesLink
} from './jobDetailModel'
import './JobDetailPage.css'

type Props = {
  job: LegacyJobData
  activeTab: JobDetailTabKey
  isDirty: boolean
  isSaving: boolean
  onTabChange: (tab: JobDetailTabKey) => void
  onJobChange: (changes: Partial<LegacyJobData>) => void
  onWorkareaChanges: (workareaGroups: WorkareaGroupWithWorkareas[]) => void
  onReloadJob: () => void
  onDetailsSaved: (saved: UpdateJobDetailsRequest) => void
  onSave: (options?: SaveJobOptions, onSaved?: () => void) => void
  onItemsChanged: () => void
  onAnalysisChange: (analysis: JobAnalysis) => void
  openReceiptId: number | undefined
  onOpenReceipt: (receiptId: number | undefined) => void
}

const tabLabels: Record<JobDetailTabKey, string> = {
  [JobDetailTabKeys.Details]: 'Details',
  [JobDetailTabKeys.Plan]: 'Plan',
  [JobDetailTabKeys.Proposals]: 'Proposals',
  [JobDetailTabKeys.Schedule]: 'Schedule',
  [JobDetailTabKeys.Visits]: 'Visits',
  [JobDetailTabKeys.Analysis]: 'Costing & Analysis',
  [JobDetailTabKeys.Invoicing]: 'Invoicing'
}

const JobDetailContent = ({
  job,
  activeTab,
  isDirty,
  isSaving,
  onTabChange,
  onJobChange,
  onWorkareaChanges,
  onReloadJob,
  onDetailsSaved,
  onSave,
  onItemsChanged,
  onAnalysisChange,
  openReceiptId,
  onOpenReceipt
}: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { visitSummary, workareaSummaries, changeSet } = useContext(ItemsContext)
  const visitsQuery = useVisitsQuery(job.id)

  const [editVisitId, setEditVisitId] = useState<string | null>(null)
  const [isAddingVisit, setIsAddingVisit] = useState(false)
  const [isAttachmentUploadOpen, setIsAttachmentUploadOpen] = useState(false)
  const [isItemsSummaryOpen, setIsItemsSummaryOpen] = useState(false)
  const [selectedAnalysisFilters, setSelectedAnalysisFilters] = useState<AnalysisFilters | undefined>()
  const [linkCustomerId, setLinkCustomerId] = useState<number | undefined>()
  const [linkCostCodeId, setLinkCostCodeId] = useState<number | undefined>()
  const [costCodeRefreshCounter, setCostCodeRefreshCounter] = useState(0)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [printWorkareas, setPrintWorkareas] = useState<PrintWorkarea[]>([])
  const [hasActiveAutomation, setHasActiveAutomation] = useState(false)
  const [isVisitNotesOpen, setIsVisitNotesOpen] = useState(false)
  const [attachmentUploadFileName, setAttachmentUploadFileName] = useState<string | undefined>()

  const isProject = job.type === ProjectTypes.Project
  const jobDetailsCleanup = useFeatureFlag(JOB_DETAILS_CLEANUP_FEATURE)
  const showReceipts = isProject
  const activeVendorsQuery = useActiveVendorsListQuery(showReceipts)
  const jobFolderAttachmentsQuery = useAttachmentsInFolder(
    showReceipts ? job.defaultAttachmentFolder : undefined,
    job.customer.id,
    job.property.id
  )

  const openReceipt = job.receipts?.find(x => x.id === openReceiptId)

  const updateReceipt = (changes: Partial<LegacyReceiptData>) => {
    if (!openReceipt) {
      return
    }
    onJobChange({ receipts: upsertReceipt(job.receipts ?? [], { ...openReceipt, ...changes }) })
  }

  const handleAddReceipt = () => {
    const receipt = createNewReceipt(job.id, job.receipts ?? [], dayjs().format('MM/DD/YYYY'))
    onJobChange({ receipts: upsertReceipt(job.receipts ?? [], receipt) })
    onOpenReceipt(receipt.id)
  }

  const handleSaveReceipt = () => {
    onSave({
      workareas: job.workareas,
      receipts: job.receipts,
      itemsChangeSet: buildItemsChangeSetForSave(changeSet, job.workareas)
    }, () => onOpenReceipt(undefined))
  }

  const printMutation = usePrintJobReportMutation(url => {
    window.open(url, '_blank')
    setIsReportModalOpen(false)
  })

  const openReportModal = () => {
    setPrintWorkareas(buildPrintWorkareas(job.workareas))
    setIsReportModalOpen(true)
  }

  const handlePrintClick = () => {
    if (isDirty) {
      onSave(undefined, openReportModal)
      return
    }
    openReportModal()
  }

  const workareas = job.workareas
  const visits = useMemo(() => visitsQuery.data ?? [], [visitsQuery.data])

  const analysisVisitRows = useMemo(
    () => buildAnalysisVisitRows(visits, workareas, visitSummary),
    [visits, workareas, visitSummary]
  )

  const defaultAnalysisFilters = useMemo(
    () => buildDefaultAnalysisFilters(workareas, analysisVisitRows),
    [workareas, analysisVisitRows]
  )

  const analysisScopeKey = useMemo(
    () => [
      workareas.filter(x => !x.isOnHold).map(x => x.id).join(','),
      analysisVisitRows.map(x => x.compoundId).join(',')
    ].join('|'),
    [workareas, analysisVisitRows]
  )

  useEffect(() => {
    setSelectedAnalysisFilters(undefined)
  }, [analysisScopeKey])

  const analysisFilters = selectedAnalysisFilters ?? defaultAnalysisFilters
  const jobAnalysis = useMemo(() => buildJobAnalysis(workareaSummaries), [workareaSummaries])

  useEffect(() => {
    onAnalysisChange(jobAnalysis)
  }, [jobAnalysis])

  const hasItemChanges = changeSet.addedItems.length > 0
    || changeSet.updatedItems.length > 0
    || changeSet.deletedItems.length > 0

  useEffect(() => {
    if (hasItemChanges) {
      onItemsChanged()
    }
  }, [hasItemChanges])

  const canAccessInvoicing = currentUser?.roles.includes('Accounting') ?? false
  const tabKeys = buildTabKeys(canAccessInvoicing)

  const showCompletedDate = isProject && job.status === ProjectStatuses.Completed
  const completedDate: CompletedDateField = {
    value: job.completedDate ? dayjs(job.completedDate) : null,
    onChange: date => onJobChange({ completedDate: date ? date.format('YYYY-MM-DDTHH:mm:ss') : null }),
  }

  const tabContent: Record<JobDetailTabKey, JSX.Element> = {
    [JobDetailTabKeys.Details]: <VSpace size="large">
      <JobDetailsForm jobId={job.id} projectAnalysis={jobAnalysis} onSave={onDetailsSaved} hasUnsavedChanges={isDirty || isSaving}
        completedDate={jobDetailsCleanup && showCompletedDate ? completedDate : undefined} />
      {!jobDetailsCleanup && showCompletedDate && <section>
        <Form layout="vertical">
          <Form.Item label="Completed Date" style={{ maxWidth: 260 }}>
            <DatePicker style={{ width: '100%' }} value={completedDate.value} onChange={completedDate.onChange} />
          </Form.Item>
        </Form>
      </section>}
    </VSpace>,
    [JobDetailTabKeys.Plan]: <JobPlan onWorkareaChanges={onWorkareaChanges} />,
    [JobDetailTabKeys.Proposals]: <section>
      <Proposals
        job={{
          id: job.id,
          name: job.name,
          type: job.type,
          estimateDate: job.estimateDate,
          salesPersonName: job.salesPersonName ?? '',
          salesPersonEmail: job.salesPersonEmail ?? '',
          customer: {
            isActive: job.customer.isActive,
            displayName: job.customer.displayName,
            emails: job.customer.emails ?? []
          }
        }}
        onAutomationChange={setHasActiveAutomation}
        onLinkCustomerToQbo={() => setLinkCustomerId(job.customer.id)}
        onLinkCostCodeToQbo={costCodeId => setLinkCostCodeId(costCodeId)}
      />
    </section>,
    [JobDetailTabKeys.Schedule]: <JobScheduleTable jobId={job.id} />,
    [JobDetailTabKeys.Visits]: <Fragment>
      <section>
        <VisitsPriceSummary />
        <Space style={{ marginTop: 16 }}>
          <ToggleCatalogBrowserButton />
          <ViewJobSummaryButton onClick={() => setIsItemsSummaryOpen(true)} />
          <AddVisitButton onClick={() => setIsAddingVisit(true)} />
        </Space>
      </section>
      <VisitsTable
        reviewedState={VisitReviewedState.NotReviewed}
        jobId={job.id}
        googleApiKey={window.env.GOOGLE_API_KEY}
        onEdit={visitId => setEditVisitId(visitId)}
      />
      <VisitsTable
        reviewedState={VisitReviewedState.Reviewed}
        jobId={job.id}
        googleApiKey={window.env.GOOGLE_API_KEY}
        onEdit={visitId => setEditVisitId(visitId)}
      />
    </Fragment>,
    [JobDetailTabKeys.Analysis]: <Fragment>
      <section className="job-detail-page__analysis-filter">
        <AnalysisFilter
          workareas={workareas}
          visits={analysisVisitRows}
          onChange={(enabledWorkareaIds, visitFilters) => setSelectedAnalysisFilters({ enabledWorkareaIds, visitFilters })}
        />
        {showVisitNotesLink(job.type) && <Typography.Link onClick={() => setIsVisitNotesOpen(true)} title="View Visit Notes">
          <Space><FontAwesomeIcon icon={faCommentAlt} />View Visit Notes</Space>
        </Typography.Link>}
      </section>
      <section>
        <JobCosting
          workareas={workareas}
          enabledWorkareaIds={analysisFilters.enabledWorkareaIds}
          visitFilters={analysisFilters.visitFilters}
        />
      </section>
      <section>
        <Typography.Title level={5}>Time Analysis</Typography.Title>
        <TimeAnalysis
          enabledWorkareaIds={analysisFilters.enabledWorkareaIds}
          visitFilters={analysisFilters.visitFilters}
        />
      </section>
      {showReceipts && <section data-testid="job-receipts">
        <Typography.Title level={5}>Receipts</Typography.Title>
        <ReceiptTable
          receipts={job.receipts ?? []}
          onAdd={handleAddReceipt}
          onClick={receiptId => onOpenReceipt(receiptId)}
          onDelete={(_event, receiptId) => onJobChange({ receipts: toggleReceiptRemoved(job.receipts ?? [], receiptId) })}
          isDirty={isDirty}
        />
      </section>}
      <section>
        <CostingAnalysisItems />
      </section>
    </Fragment>,
    [JobDetailTabKeys.Invoicing]: <JobInvoicing
      jobNeedsToBeSaved={isDirty}
      customerId={job.customer.id}
      customerQboId={job.customer.qboId}
      costCodeRefreshCounter={costCodeRefreshCounter}
      onLinkCustomerToQbo={() => setLinkCustomerId(job.customer.id)}
      onLinkCostCodeToQbo={costCodeId => setLinkCostCodeId(costCodeId)}
      onInvoicesChanged={onReloadJob}
    />
  }

  return <>
    <div id="main" className="main-project fluid-container">
      <div className="job-detail-page__body">
        <CatalogBrowser />
        <div className="job-detail-page__content">
          <JobHero
            job={job}
            isSaving={isSaving}
            isDirty={isDirty}
            onSave={() => onSave()}
            onPrint={handlePrintClick}
            onUploadAttachment={() => setIsAttachmentUploadOpen(true)}
            onLinkCustomerToQbo={() => setLinkCustomerId(job.customer.id)}
          />

          <section className="job-detail-page__tabs">
            <Tabs
              centered
              activeKey={activeTab}
              onChange={key => onTabChange(key as JobDetailTabKey)}
              items={tabKeys.map(key => ({
                key,
                label: key === JobDetailTabKeys.Proposals && hasActiveAutomation
                  ? <Space>{tabLabels[key]}<AutomationFollowUpIcon /></Space>
                  : tabLabels[key]
              }))}
            />
          </section>

          <div className="job-detail-page__tab-panel">
            {tabContent[activeTab]}
          </div>
        </div>
      </div>
    </div>

    <AttachmentUpload
      isOpen={isAttachmentUploadOpen}
      customerId={job.customer.id}
      propertyId={job.property.id}
      jobId={job.id}
      defaultFolder={job.defaultAttachmentFolder}
      defaultFileName={attachmentUploadFileName}
      onClose={() => {
        setIsAttachmentUploadOpen(false)
        setAttachmentUploadFileName(undefined)
        jobFolderAttachmentsQuery.refetch()
        onReloadJob()
      }}
    />

    <EditVisitModal
      isOpen={isAddingVisit || editVisitId !== null}
      visitId={editVisitId ?? ''}
      jobId={job.id}
      onCloseModal={() => {
        setIsAddingVisit(false)
        setEditVisitId(null)
        onReloadJob()
      }}
      googleMapsApiKey={window.env.GOOGLE_API_KEY}
      defaultLunchBreakMinutes={currentUser?.tenant.defaultLunchBreak ?? 0}
    />

    <SelectJobReportModal
      open={isReportModalOpen}
      jobReportLabel={`${projectTypeName(job.type)} Report`}
      printWorkareas={printWorkareas}
      isPrinting={printMutation.isLoading}
      onToggleWorkarea={(workareaId, print) => setPrintWorkareas(current => togglePrintWorkarea(current, workareaId, print))}
      onPrintJobReport={() => printMutation.mutate({
        routeSegment: jobPrintRouteSegment(job.type),
        jobUrl: job.url,
        workareas: buildPrintWorkareasRequest(printWorkareas)
      })}
      onPrintItemsSummary={() => {
        setIsReportModalOpen(false)
        setIsItemsSummaryOpen(true)
      }}
      onCancel={() => setIsReportModalOpen(false)}
    />

    <PrintItemsSummaryModal
      open={isItemsSummaryOpen}
      onCancel={() => setIsItemsSummaryOpen(false)}
      jobId={job.id}
      jobType={job.type}
    />

    <ReceiptModal
      open={openReceiptId !== undefined}
      receipt={openReceipt}
      vendors={activeVendorsQuery.data ?? []}
      attachments={filterReceiptAttachments(
        jobFolderAttachmentsQuery.data ?? [],
        job.defaultAttachmentFolder,
        openReceipt?.receiptNumber ?? ''
      )}
      isSaving={isSaving}
      onChange={updateReceipt}
      onUploadAttachments={() => {
        setAttachmentUploadFileName(openReceipt ? receiptAttachmentFileName(openReceipt) : undefined)
        setIsAttachmentUploadOpen(true)
      }}
      onSave={handleSaveReceipt}
      onCancel={() => onOpenReceipt(undefined)}
    />

    <VisitNotesModal
      open={isVisitNotesOpen}
      visits={visits}
      onSelectVisit={() => {
        setIsVisitNotesOpen(false)
        onTabChange(JobDetailTabKeys.Visits)
      }}
      onClose={() => setIsVisitNotesOpen(false)}
    />

    <LinkCustomerToQuickBooks
      customerId={linkCustomerId}
      customerDisplayName={job.customer.displayName}
      currentQboId={job.customer.qboId}
      currentQboDisplayName={job.customer.qboDisplayName}
      propertyList={job.customer.propertyList}
      onSuccess={result => onJobChange({
        customer: { ...job.customer, qboId: result.qboId || undefined, qboDisplayName: result.qboDisplayName || undefined }
      })}
      onClose={() => setLinkCustomerId(undefined)}
    />

    <LinkCostCodeToQuickBooks
      costCodeId={linkCostCodeId}
      onSuccess={result => {
        if (linkCostCodeId !== undefined) {
          onJobChange({ workareas: applyCostCodeQboLink(job.workareas, linkCostCodeId, result.qboId) })
        }
        setCostCodeRefreshCounter(x => x + 1)
      }}
      onClose={() => setLinkCostCodeId(undefined)}
    />
  </>
}

export default JobDetailContent
